import React, { useState, useMemo } from 'react'
import { Search, LayoutGrid, List } from 'lucide-react'
import { Input } from './ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { ScrollArea } from './ui/scroll-area'
import { OrgList } from './OrgList'
import { OrgGrid } from './OrgGrid'
import { Person, OrgNode, AssistantConnection } from '../types/profile'

interface OrganizationViewProps {
  rootNode: OrgNode
  onPersonSelect?: (person: Person) => void
  onMessage?: (person: Person) => void
  currentUserId?: string
  currentUserAccessList?: AssistantConnection[]
}

type ViewMode = 'list' | 'grid'

// Flatten org tree to array of people
const flattenOrgTree = (node: OrgNode): Person[] => {
  const people: Person[] = [node.person]
  node.children.forEach(child => {
    people.push(...flattenOrgTree(child))
  })
  return people
}

export const OrganizationView: React.FC<OrganizationViewProps> = ({
  rootNode,
  onPersonSelect,
  onMessage,
  currentUserId,
  currentUserAccessList = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [selectedPersonId, setSelectedPersonId] = useState<string | undefined>()

  // Flatten tree for grid view and search
  const allPeople = useMemo(() => flattenOrgTree(rootNode), [rootNode])

  // Filter people based on search
  const filteredPeople = useMemo(() => {
    if (!searchQuery.trim()) return allPeople

    const query = searchQuery.toLowerCase()
    return allPeople.filter(person =>
      person.name.toLowerCase().includes(query) ||
      person.title.toLowerCase().includes(query) ||
      person.department.toLowerCase().includes(query)
    )
  }, [allPeople, searchQuery])


  const handlePersonSelect = (person: Person) => {
    setSelectedPersonId(person.id)
    onPersonSelect?.(person)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with Search and View Toggle */}
      <div className="flex items-center justify-between gap-4 p-4 border-b bg-background sticky top-0 z-10">
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by name, title, or department"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-2">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList>
              <TabsTrigger value="list" className="gap-2">
                <List className="h-4 w-4" />
                List
              </TabsTrigger>
              <TabsTrigger value="grid" className="gap-2">
                <LayoutGrid className="h-4 w-4" />
                Grid
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content Area */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {viewMode === 'list' ? (
            <OrgList
              people={filteredPeople}
              selectedPersonId={selectedPersonId}
              onPersonSelect={handlePersonSelect}
              onMessage={onMessage}
              currentUserAccessList={currentUserAccessList}
            />
          ) : (
            <OrgGrid
              people={filteredPeople}
              selectedPersonId={selectedPersonId}
              onPersonSelect={handlePersonSelect}
              onMessage={onMessage}
              currentUserAccessList={currentUserAccessList}
            />
          )}
        </div>
      </ScrollArea>

      {/* Results Count */}
      {searchQuery && (
        <div className="px-4 py-2 border-t bg-muted/50 text-sm text-muted-foreground">
          Found {filteredPeople.length} {filteredPeople.length === 1 ? 'person' : 'people'}
        </div>
      )}
    </div>
  )
}

